import React, { useState, useEffect } from 'react';
import './styles.css';
import { Flex, View } from '@smarterservices/smarter-ui';
import Timeline from './Timeline';
import AnnotationOptions from './Annotations/AnnotationOptions';
import AnnotationLabels from './Annotations/AnnotationLabels';
import PlaybarTimer from './Timer';
import { getStore, usePlayerStore, useAppStore } from '../../../stores/StoreContext';
import RenderPopover from './Annotations/Popover';
import Volume from './Volume';

/**
 * Renders the video controls bar.
 *
 * The controls bar is a container for several controls, including a timeline,
 * play/pause button, timer, annotation options, and annotation labels.
 *
 * @returns {React.ReactElement} The controls bar.
 */
const Controls = () => {
  const LayoutStore = getStore('layoutStore');
  const [showTimeline, setShowTimeline] = useState(false);

  // We can only show this if the video is ready to play...
  const hlsPlaybackReady = usePlayerStore((state) => state.hlsPlaybackReady);
  const enabledComponents = useAppStore((state) => state.enabledComponents);
  const canHaveLiveVideo = usePlayerStore((state) => state.canHaveLiveVideo);

  // Delay Timeline rendering by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTimeline(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // do not return anything if the video is not ready to play and live video is not possible...
  if (!hlsPlaybackReady && !canHaveLiveVideo) {
    return null;
  }

  // do not return anything if the timeline is not enabled...
  if (!enabledComponents.timeline || !enabledComponents.controls) {
    return null;
  }

  return (
    <View as="div" id="video-control-bar-wrapper">
      <Flex
        as="div"
        direction="row"
        withVisualDebug={false}
        id="video-controls"
        alignItems="center"
        elementRef={(el) => {
          LayoutStore.setMediaControlsRef(el);
        }}
      >
        <Flex.Item shouldGrow align="start">
          {showTimeline && <Timeline />}
        </Flex.Item>
        <Flex.Item padding="none none none x-small" align="start">
          <View as="div" height="40px" padding="xx-small 0 0 0">
            <PlaybarTimer />
            <Volume />
            <AnnotationOptions />
          </View>
          <AnnotationLabels />
        </Flex.Item>
      </Flex>
      {hlsPlaybackReady && <RenderPopover />}
    </View>
  );
};

export default Controls;
